import React, { useEffect, useMemo, useState } from "react";
import { addMonths, format, isAfter, isBefore, parse, startOfMonth, startOfToday } from "date-fns";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import "react-day-picker/dist/style.css";

const parseDateValue = (value: string) => parse(value, "yyyy-MM-dd", new Date());

interface DatePickerModalProps {
    open: boolean;
    value: string;
    onChange: (value: string) => void;
    onClose: () => void;
    title?: string;
    description?: string;
    selectedLabel?: string;
    confirmLabel?: string;
    minDate?: Date;
    maxDate?: Date;
}

export default function DatePickerModal({
    open,
    value,
    onChange,
    onClose,
    title = "Choose a date",
    description = "Select a date.",
    selectedLabel = "Selected",
    confirmLabel = "Done",
    minDate,
    maxDate,
}: DatePickerModalProps) {
    const minSelectableDate = minDate ?? startOfToday();
    const maxSelectableDate = maxDate;
    const selectedDate = value ? parseDateValue(value) : undefined;
    const [calendarMonth, setCalendarMonth] = useState<Date>(selectedDate ?? minSelectableDate);
    const defaultClassNames = getDefaultClassNames();
    const minDateTime = minDate?.getTime();
    const initialCalendarMonth = useMemo(() => {
        if (value) {
            return parseDateValue(value);
        }

        return minDateTime ? new Date(minDateTime) : startOfToday();
    }, [value, minDateTime]);
    const canGoToPreviousMonth = isAfter(startOfMonth(calendarMonth), startOfMonth(minSelectableDate));
    const canGoToNextMonth = maxSelectableDate
        ? isBefore(startOfMonth(calendarMonth), startOfMonth(maxSelectableDate))
        : true;

    useEffect(() => {
        if (!open) {
            return;
        }

        setCalendarMonth(initialCalendarMonth);
    }, [initialCalendarMonth, open]);

    useEffect(() => {
        if (!open || typeof document === "undefined") {
            return;
        }

        const { overflow } = document.body.style;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = overflow;
        };
    }, [open]);

    if (!open) {
        return null;
    }

    const handleDateSelection = (nextDate: Date | undefined) => {
        if (!nextDate) {
            return;
        }

        onChange(format(nextDate, "yyyy-MM-dd"));
        setCalendarMonth(nextDate);
    };

    const handleJumpToToday = () => {
        const today = startOfToday();
        setCalendarMonth(today);
        onChange(format(today, "yyyy-MM-dd"));
    };

    return (
        <div className="fixed inset-0 z-50 md:flex md:items-center md:justify-center md:p-6">
            <button
                type="button"
                aria-label="Close picker"
                className="absolute inset-0 bg-slate-950/45 backdrop-blur-[1px]"
                onClick={onClose}
            />

            <div
                role="dialog"
                aria-modal="true"
                className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-md rounded-t-[2rem] bg-white px-4 pb-5 pt-4 shadow-[0_-24px_80px_rgba(15,23,42,0.22)] md:relative md:inset-auto md:max-h-[90vh] md:max-w-[26rem] md:overflow-y-auto md:rounded-[1.75rem] md:px-5 md:pb-5 md:pt-4 md:shadow-[0_28px_90px_rgba(15,23,42,0.18)]"
            >
                <div className="mx-auto mb-4 h-1.5 w-14 rounded-full bg-slate-200 md:hidden" />

                <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                        <p className="text-sm font-semibold text-slate-900">{title}</p>
                        <p className="mt-1 text-sm text-slate-900">{description}</p>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="space-y-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">{selectedLabel}</p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">
                            {value ? format(parseDateValue(value), "EEE, MMM d, yyyy") : "No date selected"}
                        </p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="mb-3 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={() => setCalendarMonth((currentMonth) => addMonths(currentMonth, -1))}
                                disabled={!canGoToPreviousMonth}
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-300 disabled:hover:bg-white"
                                aria-label="Previous month"
                            >
                                <ChevronLeft size={16} strokeWidth={1.75} />
                            </button>

                            <p className="text-sm font-semibold text-slate-900">
                                {format(calendarMonth, "MMMM yyyy")}
                            </p>

                            <button
                                type="button"
                                onClick={() => setCalendarMonth((currentMonth) => addMonths(currentMonth, 1))}
                                disabled={!canGoToNextMonth}
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-300 disabled:hover:bg-white"
                                aria-label="Next month"
                            >
                                <ChevronRight size={16} strokeWidth={1.75} />
                            </button>
                        </div>

                        <DayPicker
                            mode="single"
                            selected={selectedDate}
                            onSelect={handleDateSelection}
                            month={calendarMonth}
                            onMonthChange={setCalendarMonth}
                            showOutsideDays
                            disabled={{
                                before: minSelectableDate,
                                ...(maxSelectableDate ? { after: maxSelectableDate } : {}),
                            }}
                            hideNavigation
                            numberOfMonths={1}
                            className="mx-auto"
                            classNames={{
                                ...defaultClassNames,
                                root: `${defaultClassNames.root} w-full`,
                                months: "flex justify-center",
                                month: "w-full space-y-4",
                                caption: "hidden",
                                weekday:
                                    "text-[0.68rem] font-medium uppercase tracking-[0.16em] text-slate-400",
                                head_cell: "w-10 text-center",
                                table: "mx-auto border-separate border-spacing-y-1",
                                outside: "text-slate-300",
                                disabled: "text-slate-300 opacity-50",
                                today: "font-semibold text-blue-600",
                                selected: "bg-blue-600 text-white hover:bg-blue-600 focus:bg-blue-600 [&>button]:text-white",
                                day: "text-center",
                                day_button:
                                    "h-10 w-10 rounded-full text-sm font-medium text-slate-700 hover:bg-blue-50 disabled:hover:bg-transparent",
                            }}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-1">
                        <button
                            type="button"
                            onClick={handleJumpToToday}
                            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                        >
                            Jump To Today
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-2xl bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                        >
                            {confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
